/**
 * Figma:
 * - Thank you - Default: https://www.figma.com/design/NBnn0MEdCHdbMj2LsWQqng/Consumer-App?node-id=11176-89228
 * - Thank you - Default (variant): https://www.figma.com/design/NBnn0MEdCHdbMj2LsWQqng/Consumer-App?node-id=11176-91110
 * - Thank you - Default (variant 2): https://www.figma.com/design/NBnn0MEdCHdbMj2LsWQqng/Consumer-App?node-id=11352-23086
 */
import { preloadQuery } from "@relay";
import { EmptyCard3 } from "@shadcn/pro-blocks/application/empty-sections/empty-section-4";
import { Button } from "@shadcn/ui/button";
import { Separator } from "@shadcn/ui/separator";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { MapPin } from "lucide-react";
import { usePreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";
import z from "zod";
import { AppHeader } from "~/components/layout/AppHeader";
import { PageContainer } from "~/components/layout/PageContainer";
import { SncProductCard } from "~/components/SncProductCard";
import { useCart } from "~/contexts/CartContext";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { formatToCurrency } from "@utils/currency";
import { applyTheme, getCurrentTheme } from "@utils/themes";
import { thankYouSncOrderQuery } from "./__generated__/thankYouSncOrderQuery.graphql";

const query = graphql`
	query thankYouSncOrderQuery($clientReferenceId: String!) {
		sncOrder(by: { clientReferenceId: $clientReferenceId }) @required(action: THROW) {
			id
			status
			amount
			createdAt
			hub {
				id
				meta @required(action: THROW) {
					id
					name
					street
					city
					postalCode
				}
				sncProducts(filter: { active: true }, pagination: { page: 0, pageSize: 4 }) {
					nodes {
						id
						stock
						...SncProductCardFragment
					}
				}
			}
			organization {
				id
				name
				supportEmail
				logo {
					id
					variant(variant: PUBLIC)
				}
			}
			lineItems(pagination: { page: 0, pageSize: 1000000 }) {
				nodes {
					id
					priceAmount
					sncProduct {
						id
						product {
							id
							name
						}
					}
				}
			}
		}
	}
`;

export const Route = createFileRoute("/$hubMetaName/shop/thank-you")({
	beforeLoad: () => {
		document.title = "bacehub | Order Confirmed";
	},
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			checkoutId: z.string(),
		})
	),
	loaderDeps: ({ search: { checkoutId } }) => ({ checkoutId }),
	loader: async (ctx) => {
		return preloadQuery<thankYouSncOrderQuery>(ctx.context.relayEnvironment, query, {
			clientReferenceId: ctx.deps.checkoutId,
		});
	},
});

export default function RouteComponent() {
	const { sncOrder } = usePreloadedQuery<thankYouSncOrderQuery>(query, Route.useLoaderData());
	const i18n = useI18nContext().LL["new-ui"];
	const useCartStore = useCart();

	// SNC always uses default theme
	if (getCurrentTheme() !== "default") {
		applyTheme("default");
	}

	// Clear cart on mount
	useCartStore.getState().clearCart();

	const navigate = useNavigate();
	const hubMeta = sncOrder.hub.meta;
	const org = sncOrder.organization;
	const orgLogo = org.logo?.variant ?? "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400";

	return (
		<PageContainer
			footer={
				<Button size="lg" className="w-full" asChild>
					<Link to="/$hubMetaName/shop" params={{ hubMetaName: hubMeta.name }}>
						<T>{i18n.BACK_TO_HOME()}</T>
					</Link>
				</Button>
			}
		>
			<AppHeader service="ALL" />

			{/* Header text */}
			<div className="px-6 pt-4">
				<h1 className="text-2xl font-breit">
					<T>{i18n.ALL_DONE()}</T>
				</h1>
				<h1 className="text-2xl font-breit">
					<T>{i18n.THANK_YOU_BACE()}</T>
				</h1>
			</div>

			{/* Order section - grey background */}
			<div className="bg-[#F4F4F5] rounded-xl py-4 mx-6 flex flex-col gap-4">
				{/* Your order label */}
				<p className="text-sm font-medium font-breit px-6">
					<T>{i18n.YOUR_ORDER()}</T>
				</p>

				{/* Org image */}
				<div className="px-6">
					<img src={orgLogo} alt={org.name} className="w-full h-20 object-cover rounded-lg" />
				</div>

				{/* Location */}
				<div className="px-6">
					<p className="text-sm font-medium font-breit mb-2">
						<T>{i18n.LOCATION()}</T>
					</p>
					<EmptyCard3
						icon={MapPin}
						title={hubMeta.name}
						description={
							<>
								{hubMeta.street}
								<br />
								{hubMeta.postalCode} {hubMeta.city}
							</>
						}
					/>
				</div>

				{/* Line items */}
				<div className="px-6 space-y-2">
					{sncOrder.lineItems.nodes.map((item) => (
						<div key={item.id} className="flex justify-between text-sm">
							<span>{item.sncProduct.product.name}</span>
							<span>{formatToCurrency(item.priceAmount)}</span>
						</div>
					))}
				</div>

				{/* Total */}
				<div className="px-6">
					<Separator className="mb-4" />
					<p className="text-sm font-medium font-breit flex justify-between">
						<span>
							<T>{i18n.TOTAL()}</T>
						</span>
						<span>{formatToCurrency(sncOrder.amount)}</span>
					</p>
				</div>

				{/* Seller support */}
				{org.supportEmail && (
					<div className="px-6 pt-2">
						<p className="text-sm text-muted-foreground">{i18n.SELLER_SUPPORT_TEXT({ email: org.supportEmail })}</p>
					</div>
				)}
			</div>

			{/* More products section */}
			{sncOrder.hub.sncProducts.nodes.length > 0 && (
				<>
					<div className="px-6 pt-4">
						<h2 className="text-2xl font-breit">
							<T>{i18n.MORE_PRODUCTS()}</T>
						</h2>
					</div>
					<div className="px-6 pb-6 grid grid-cols-2 gap-4">
						{sncOrder.hub.sncProducts.nodes.map((product) => (
							<SncProductCard
								key={product.id}
								product={product}
								showAddButton={false}
								onProductClick={(sncProductId) =>
									navigate({
										to: "/$hubMetaName/shop",
										params: { hubMetaName: hubMeta.name },
										search: { sncProductId },
									})
								}
							/>
						))}
					</div>
				</>
			)}
		</PageContainer>
	);
}
